(ns heraldicon.frontend.news
  (:require
   [heraldicon.frontend.title :as title]
   [re-frame.core :as rf]))

(defn- release-image [img-src & {:keys [width]}]
  [:a {:href img-src
       :target "_blank"}
   [:img {:style {:width (or width "100%")}
          :src img-src
          :alt "release update overview"}]])

(defn post-date [{:keys [id date]}]
  (or date (subs id 0 10)))

(def posts
  [{:id "2026-10-31"
    :title "Faster, bigger, better exports"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:p "Over the years I've added many features to Heraldicon, while trying to keep performance up. Nevertheless some features added complexity and processing time, which also forced me to lower export resolution a while ago. I always had a few optimizations planned, but they'd require daunting rewrites of the code."]
         [:p "Today we have AI, and this sort of thing is one area where it can really help. So in the last three days I went through a bunch of rewrites and optimizations, and they've paid off nicely!"]
         [:ul
          [:li "SVG exports: " [:b "up to 4 times faster"]]
          [:li "SVG size: " [:b "up to 4 times smaller"]]
          [:li "SVGs a little easier to edit in SVG tools"]
          [:li "PNG exports and previews: " [:b "up to 4 times faster"]]
          [:li "PNG quality is sharper due to better rasterization"]
          [:li "Rendering in the editor: " [:b "sharper"] ", and interaction should feel snappier for complex arms"]]
         [:p "A lot of that is due to less repetition in the SVG data, which also makes it cleaner and easier to work with in editors; it's still challenging, but it should be much easier now, especially with plain tincture fields. The PNGs look sharper because there's less masking now, it's very noticeable on zoom. But even better! Since these exports are fast now, I've increased the size, making them even better quality:"]
         [:ul
          [:li "Coats of arms export to a width of " [:b "1000 pixels, making them 4 times bigger"] "."]
          [:li "Collections export to a width of " [:b "3000 pixels, making them 9 times bigger"] ". (Note: a maximal height of 10,000 pixels, it's a technical limitation.)"]]]
        [:div
         (release-image "/img/2026-10-31-release-update.png" :width "520px")]]])}

   {:id "2026-08-19"
    :title "Better exports"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:p "SVG and PNG exports always were a bit awkward. They opened in a new tab, they had awful filenames, and you had to know how to save them as files in your browser."]
         [:p "But no more! Now they're more convenient: they'll automatically download after they've been generated, and they'll have proper filenames based on the arms/collection name and the export function chosen."]]]])}

   {:id "2026-06-24"
    :title "Bug fix: partitioned fields respect the escutcheon's field better"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:p "Some escutcheons have small nibs or notches in their outline that aren't really part of the field — for example the little feet at the bottom of the " [:b "French modern"] " escutcheon, or the notched top corners of the " [:b "English"] " one. Each of these escutcheons already defines a " [:em "field area"] " (a bounding box and fess point) that excludes those extremities, and ordinaries and charges have always honoured it."]
         [:p "Partitions did not. When a field was partitioned (e.g. " [:em "quarterly"] ", " [:em "per pale"] ", " [:em "per saltire"] ", …) the sub-fields were derived from the raw outline, so those nibs could stretch a sub-field's area and pull its fess point — and any charges placed within it — slightly off. Divisions now use the escutcheon's field area for the sub-fields, while the outline itself (nibs included) is still drawn and tinctured exactly as before."]
         [:p
          [:span {:style {:color "darkred"}} [:b "Some of your coats of arms may look different now if they use a partitioned field on one of these escutcheons"]] "."]
         [:p "Please have a look. Perhaps you already noticed the inaccuracy yourself and placed things manually, then that won't look right anymore. Resetting back to the defaults is a good idea."]
         [:p " The effect is most visible on " [:b "French modern"] " and " [:b "English"] ", but the escutcheons that define such a custom field area, and could therefore be affected, are: " [:b "French modern"] ", " [:b "English"] ", Polish, Polish 19th century, Renaissance, Flag, Rounded Norman (late), Square Iberian Engrailed, Kalasag, Louisiana, French Slim, Bulgy Heater, Rounded Heater, Pointed Heater, Angular, Scutum, Triangular, NRW, Sleek Pointy, Gonfalon, Orcish, Orcish2, Dwarven, Gnomish, Halfling, Halfling Pavise, Owlfolk, Arrowhead, Dragonborn, Draped Pointy Banner, Embattled Heater, Embattled Iberian, and Pauldron. Escutcheons without such extremities, and any arms with an undivided field, are unaffected."]]
        [:div
         (release-image "/img/2026-06-24-release-update.png" :width "520px")]]])}

   {:id "2026-06-15"
    :title "Structured search for arms"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:p "The arms list now has a " [:b "structured search"] " that lets you filter by specific aspects of the design using " [:code "key:value"] " tokens — mixed freely with regular name search. All tokens have to match for an arms to show up."]
         [:p "Just type into the search bar. The autocomplete dropdown walks you through the available keywords and their values. For " [:code "charge:"] " and " [:code "crest:"] " it pops up the same charge-type tree you know from the charges list, narrowed live by whatever you type after the colon."]
         [:p "There's also a help icon next to the search input that summarises the available keywords."]]
        [:div
         (release-image "/img/2026-06-15-release-update-1.png" :width "500px")]]

       [:div.release-row
        [:div.info
         [:h3 "Available keywords"]
         [:ul
          [:li [:code "tincture:"] " — any tincture anywhere in the arms (the field, an ordinary, a charge, a cottise, …). " [:em "Example: "] [:code "tincture:or"] "."]
          [:li [:code "partition:"] " — a field's division or pattern. " [:em "Example: "] [:code "partition:per-pale"] ", " [:code "partition:chequy"] "."]
          [:li [:code "field:"] " — any property of any field — its type " [:em "or"] " its tincture. " [:em "Example: "] [:code "field:chequy"] ", " [:code "field:gules"] "."]
          [:li [:code "main-field:"] " — same as " [:code "field:"] " but only for the top-level main field (including " [:code "main-field:plain"] ")."]
          [:li [:code "charge:"] " — any charge in the arms. Uses the full charge-type hierarchy, so " [:code "charge:mammal"] " matches all mammals, " [:code "charge:lion"] " just lions, etc."]
          [:li [:code "crest:"] " — like " [:code "charge:"] " but restricted to charges used as crests on helms."]
          [:li [:code "ordinary:"] " — any ordinary (e.g. " [:code "ordinary:bend"] ", " [:code "ordinary:fess"] ")."]
          [:li [:code "ornament:"] " — any ornament: " [:code "ornament:motto"] ", " [:code "ornament:helm"] ", " [:code "ornament:crown"] ", " [:code "ornament:mantling"] ", " [:code "ornament:supporter"] ", etc."]
          [:li [:code "attitude:"] " — a charge's attitude (e.g. " [:code "attitude:rampant"] ", " [:code "attitude:passant"] ")."]]]
        [:div
         (release-image "/img/2026-06-15-release-update-2.png" :width "500px")]]])}

   {:id "2026-05-15"
    :title "Deletion feature"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:p "You can now " [:b "delete"] " your arms, collections, charges, and ribbons. The action lives in the " [:b "..."] " menu next to the Save button on each entity's page, and asks for confirmation before doing anything."]
         [:p "Collections and arms are always permanently deleted. If the deleted arms were part of someone's collection, that slot is preserved with a " [:em "(deleted)"] " label so the collection layout stays intact."]
         [:p "Charges and ribbons are a little different. If no other arms use them, they're permanently deleted. If they " [:em "are"] " used, they're detached from your account and kept anonymously, so the arms that rely on them an still be displayed. Either way, the entity's id can never be reassigned, so old shared links still resolve predictably."]
         [:p "Each entity's edit page now also shows a " [:b "Used in"] " panel listing the entities that depend on it, with private dependents counted but not named."]]
        [:div
         (release-image "/img/2026-05-15-release-update.png" :width "300px")]]])}

   {:id "2026-05-11"
    :title "Profile pictures"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:p "You can now upload your own " [:b "profile picture"] ". Head to your account page and click the new " [:b "Upload"] " button next to your avatar, pick an image, drag it around and zoom to position it the way you like, and save. The crop is circular by default, but you can opt out of that with a checkbox if you'd rather show the whole image as a square. This is particularly useful if you want to use a coat of arms with transparent background."]
         [:p "As a consequence, " [:b "Gravatar is no longer supported"] ". Avatars that were previously sourced from Gravatar will fall back to a default placeholder until you upload your own."]]
        [:div
         (release-image "/img/2026-05-11-release-update.png" :width "300px")]]])}

   {:id "2026-04-08"
    :title "New line style: stepped"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:p "This update adds the " [:b "stepped"] " line style (German: " [:em "Stufenschnitt"] "). Unlike other line styles, the stepped line keeps its horizontal segments truly horizontal and its vertical segments truly vertical, regardless of the line's angle."]
         [:p "This means a horizontal line will appear straight, as there are no steps to show. The steps only become visible when the line is at an angle, such as for a bend, chevron, or saltire. The greater the angle, the more pronounced the staircase effect."]]
        [:div
         (release-image "/img/2026-04-08-release-update.png" :width "250px")]]])}

   {:id "2026-04-03"
    :title "New line styles: epine, trefly, trefly counter trefly"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:p "This update adds three new line styles:"]
         [:ul
          [:li "trefly"]
          [:li "trefly counter trefly"]
          [:li "epine"]]]
        [:div
         (release-image "/img/2026-03-30-release-update.png" :width "500px")]]])}

   {:id "2026-03-30"
    :title "Charge type filter, new line styles, new partitions, mounts"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:p "The community has contributed more themes and escutcheons, which now have a label and can be filtered by name. There also have been various bugfixes and minor improvements."]
         [:p "Four of the biggest features are the following:"]]]

       [:div.release-row
        [:div.info
         [:h3 "Charge type filter"]
         [:p "The charge list now has a " [:b "charge type filter"] " that lets you browse charges by their type in the ontology hierarchy. This makes it much easier to find the right charge when you know what category you're looking for, e.g. narrowing down to just birds or mammals."]]
        [:div
         (release-image "/img/2026-03-30-release-update-charge-type-filter.png")]]

       [:div.release-row
        [:div.info
         [:h3 "Mount and trimount charges"]
         [:p "There are now dedicated " [:b "mount"] " and " [:b "trimount"] " charges. These are properly recognized by the blazon parser, so blazons like " [:em "Azure, a trimount Or"] " will place the charge correctly at the base of the shield."]]
        [:div
         (release-image "/img/2026-03-30-release-update-mount.png")]]

       [:div.release-row
        [:div.info
         [:h3 "New line styles"]
         [:p "Five new line styles have been added:"]
         [:ul
          [:li "indented pometty"]
          [:li "lilyous"]
          [:li "spaded"]
          [:li "bastionné"]
          [:li "palissado"]]]
        [:div
         (release-image "/img/2026-03-30-release-update-line-styles.png" :width "500px")]]

       [:div.release-row
        [:div.info
         [:h3 "New partition patterns"]
         [:p "Two new partition patterns are now available: " [:b "scaly"] " and " [:b "plumetty"] "."]]
        [:div
         (release-image "/img/2026-03-30-release-update-partitions.png" :width "200px")]]])}

   {:id "2026-03-26"
    :title "User database migration"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:p "You can now log in with your " [:b "username"] " or " [:b "email address"] ". For this to work I've migrated the system to a different one. This shouldn't be noticeable to you, but if you notice any issues with registrations, password resets, or confirmation emails, then please reach out to me, so I can have a look."]
         [:p "Note: in the past it was possible to register multiple users with the same email address, this is no longer supported. The old accounts remain, but in those cases you need to log in with the username."]]]])}

   {:id "2026-01-01"
    :title "Happy new year!"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:p "I wish all of you a happy and healthy 2026!"]
         [:p "Thank you for all your continued work on the site. Heraldicon has grown significantly again:"]
         [:ul
          [:li "over 7,300 users"]
          [:li "almost 100,000 coats of arms created, over 8,000 of them public"]
          [:li "over 15,000 charges, over 5,500 of them public"]]
         [:p "The vast majority of content remains private. " [:b "If you've created something you're proud of, please consider making it public"] " - it allows other users to enjoy or even use your work!"]
         [:p "As always, thanks to everyone who contributed with feedback, feature requests, public charges and arms, community themes, escutcheons, and all the other ways you support this project."]]]])}

   {:id "2025-01-01-ontology"
    :date "2025-01-01"
    :title "Charge type ontology and better search"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:h3 "Charge type ontology"]
         [:p "The charge type attribute (previously also called 'blazonry name') is no longer a free text field, but a dynamic select field of charge types in a hierarchy."]
         [:p "The hierarchy is based on the one " [:a {:href "https://dev.finto.fi/hero/en/page/p6"} "HERO - Ontology of Heraldry"] ", but there are others that were already added by users or that I found on other sites while curating the existing set, most notably "
          [:a {:href "https://mistholme.com"} "Mistholme"] "."]
         [:p "Many types provided by users didn't quite fit into this ontology, for instance because they included attitudes, tinctures, etc., but also because the concepts were not yet part of the ontology. "
          "I have added a lot of them, renamed many, but also grouped many into more general concepts or simply moved them to 'unknown'. "
          [:span {:style {:color "darkred"}} [:b "If this happened to your charge, then please have a look whether you find a more fitting charge type in the tree now."]]]
         [:p "Since these charge types are also used for the blazonry parser and generator, I realize that they won't include the full description of the charge itself. I hope to address this at some point, in the meantime you can use the 'manual blazon' feature on every element."]
         [:p "I also plan to add a way to search via a tree in the charge select component, but you can already do a text search for charge types."]]

        [:div
         (release-image "/img/2025-01-01-release-update.png")]]
       [:div.release-row
        [:div.info
         [:h3 "Improved search"]
         [:p "The search now allows you to use quotes to include spaces and match full words, as well as regular expressions for power users. Examples:"]
         [:ul
          [:li [:code "\"arm\""] " - will only match the word 'arm', but not 'arms' or other words containing 'arm'"]
          [:li [:code "\"coat of arms\""] " - will only match that complete string, not entities that just happen to match all those words in any order or position"]
          [:li [:code "/^[unicorl]+$/"] " - will match any string containing only those letters, for instance the charges 'lion' and 'unicorn'"]
          [:li [:code "/(saint|soldier)/"] " - will match any entity containing 'saint' or 'soldier' (or both)"]]
         [:p "Note: all searches are case insensitive."]]
        [:div
         (release-image "/img/2025-01-01-release-update-2.png")]]])}

   {:id "2025-01-01"
    :title "Happy new year!"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:p "I hope all of you had a great 2024, and I wish you an even better 2025!"]
         [:p "Just two days ago Heraldicon reached a milestone of 4,000 registered users. Amazing! And for comparison with the last new year post, there now are:"]
         [:ul
          [:li "50,000+ arms, over 5,000 of them public"]
          [:li "10,000+ charges, over 4,000 of them public"]
          [:li "1,200+ collections, over 170 of them public"]]
         [:p "So again thanks to everybody who helped with feedback, feature requests, charge creations, especially public ones, community themes, community escutcheons, and all the other ways many of you contributed!"]]]])}

   {:id "2024-02-25"
    :title "Drag and drop for components and collection arms"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:p "This was long overdue, and I never was happy with those little 'up'/'down' arrows to manipulate component order. Now you can drag and drop components and collection arms to reorder them."]
         [:p "You can now also move components between parts of your achievement, e.g. move a charge from a helm to a field or to ornaments and back."]
         [:p "Finally, you can drag and drop subfields in a field partition."]]]])}

   {:id "2024-01-26"
    :title "Vairy/lozengy/potenty/papellony/masony rendering improved"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:p "A user informed me that vairy in particular wasn't rendered correctly. The tinctures, as given in the blazon, were swapped. This affected all variants of the field type."]
         [:p "While fixing this I also noticed that a similar issue existed for lozengy, potenty, papellony, and masony. This has been fixed as well."]
         [:p [:span {:style {:color "darkred"}} [:b "Some of your coats of arms may look different now if they use these field types"]]]
         [:p "Please have a look and adjust your arms if necessary, I apologize for the inconvenience."]]]])}

   {:id "2024-01-12"
    :title "Happy new year!"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:p "I wish you all health and a splendid upcoming year."]
         [:p "There weren't as many visible changes this year, but there were several improvements in the backend and a lot of new arms and charges created by many of you:"]
         [:ul
          [:li "30,000+ arms, over 3,000 of them public"]
          [:li "7,000+ charges, over 3,000 of them public"]
          [:li "600+ collections, over 100 of them public"]]
         [:p "Just as last year: thanks to everybody who helped with feedback, feature requests, charge creations, especially public ones, community themes, community escutcheons, and all the other ways many of you contributed!"]]]])}

   {:id "2023-03-19"
    :title "Bordure/orle line style adjustments and flory line styles"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:p "Flory and flory-counter-flory are now available line styles."]
         [:p "Introducing those new line styles made me realize that the previous offsetting and spacing of line styles for bordures and orles was a bit unpredictable and difficult. This is now greatly improved, I believe. With a reasonable default offset being chosen to align the line style with the top center of the surrounding field shape."]
         [:p
          [:span {:style {:color "darkred"}} [:b "Some of your coats of arms may look different now if they use orles or bordures with line styles."]] "."]
         [:p "Please have a look and maybe adjust ordinaries or charges if necessary, unfortunately this was another case that was difficult to migrate without breaking some existing arms."]]]])}

   {:id "2023-01-12"
    :title "Escutcheon adjustments"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:p "I just went over all the existing escutcheons and improved their shapes and how they define the field and where the fess point is."]
         [:p "This changed some of the escutcheons quite a bit, and even tho the vast majority of existing coats of arms is not affected, "
          [:span {:style {:color "darkred"}} [:b "some of your coats of arms may look different now if they use these escutcheons"]] "."]
         [:p "Please have a look and maybe adjust ordinaries or charges if necessary, I apologize for the inconvenience. This kind of change should be a very rare event."]]]])}

   {:id "2023-01-05"
    :title "Happy new year!"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:p "I hope everybody had a good start into the year."]
         [:p "Heraldicon recently reached a few milestones, there are now:"]
         [:ul
          [:li "over 1,200 users"]
          [:li "over 1,700 public arms, over 10,000 private arms"]
          [:li "over 1,000 public charges, over 3,200 private charges"]
          [:li "over 50 public collections, over 280 private collections"]]
         [:p "Thanks to everybody who helped with feedback, feature requests, charge creations, especially public ones, community themes, community escutcheons, and all the other ways many of you contributed!"]]]])}

   {:id "2022-09-01"
    :title "New rendering engine and various improvements"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:p "There have been various new small features over the past few months and a large rewrite of the rendering system, which improves some existing conditions and will allow many planned features that weren't possible in the previous version."]
         [:p "New features and changes:"]
         [:ul
          [:li [:b "new rendering engine"] " - this is the biggest change, albeit largely invisible, some immediate differences are:"
           [:ul
            [:li "components are much more aware of their surrounding field's environment and shape"]
            [:li "line styles now detect the exact intersection with the shape and by default use the line length as a base for their width option; a new 'Size reference' option for lines allows configuration of this"]
            [:li "exact bounding boxes for all components can now be calculated, which allows more precise resizing of the achievement, based on its helms, ornaments, etc."]
            [:li "cottises inherit the parent ordinary's properties, synching their line styles with the ordinary"]
            [:li "couped/voided ordinaries can now be fimbriated"]
            [:li [:b "some of these changes are not fully backwards compatible"] ", so if you see changes in your arms, especially in partitions, ordinaries, cottises, line styles, or fimbriation, then that may be why; I try to migrate data, but sometimes that's not feasible... and hopefully these cases can be fixed easily"]]]
          [:li "a 'Gyronny N' field partition for an arbitrary number of subfields"]
          [:li "a mechanism to load arms into a field (in the top right of the field form), e.g. to use existing arms in subfields"]
          [:li "many new escutcheons and some new themes, mostly provided by the community on the Discord server, thanks!"]
          [:li "sort options for lists, as well as a counter of accessible and filtered items"]
          [:li "'Copy to new', exporting, and sharing are now grouped in a new action button"]
          [:li "'Copy to new' now also works with items of other users, automatically filling in the appropriate source attribution"]
          [:li "improved licensing and attribution, also fixed a bug where BY-NC-SA and BY-SA were compatible"]
          [:li "new anchor/orientation points:"
           [:ul
            [:li [:b "center"] " - the exact middle of the field's bounding box, often coincides with 'fess' on subfields"]
            [:li [:b "hoist"] " - a point left of the center for flags, similar to 'fess' in arms"]
            [:li [:b "fly"] " - a point right of the center for flags, opposite 'hoist'"]]]
          [:li "allow fimbriation for cottises"]
          [:li "new charge detail view, separating the original SVG view from the preview"]
          [:li "improved blazonry editor, it understands more concepts now and there's a Discord bot that can use it"]
          [:li "a few new tincture modifiers: flagged, feathered"]
          [:li "a few more tooltips to help with the UI"]
          [:li "some modals for login/logout/confirmation were buggy, that has been fixed"]
          [:li "various bugfixes"]]]]])}

   {:id "2022-04-14"
    :title "Change of 'origin'/'anchor' naming"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:p "The names for the two points used to position and orient partitions, ordinaries, and charges, have been renamed."]
         [:ul
          [:li [:b "anchor"] " (previously " [:em "origin"] "): is the main point an element is positioned with."]
          [:li [:b "orientation"] " (previously " [:em "anchor"] "): is the point an element orients itself towards, but it can also be a fixed angle."]]
         [:p "This might be confusing for a while for anyone already used to the old names, but I think it more accurately describes these concepts and simplifies using them. I hope it won't cause too much trouble."]]]])}

   {:id "2022-04-07"
    :title "Blazonry reader, translations, bordures with line styles, metadata"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:p "The main new feature is a blazonry reader, you can click on the pen nib next to any field to invoke it or click the dedicated button to create new arms from a blazon. Features include:"]
         [:ul
          [:li "immediate rendering of the blazon, feedback if a word is not understood"]
          [:li "auto completion suggestions for any incomplete or malformed blazon"]
          [:li "support for all charges added by the community, your blazonry reader will understand all publically available charges and your private ones"]]
         [:p "supported concepts:"]
         [:ul
          [:li "partitions with sub fields and line styles, e.g. 'quartered i. and iv. azure, ii. and iii. or'"]
          [:li "ordinaries with line styles, fimbriation, and cottising, e.g. 'or, a fess wavy fimbriared gules argent'"]
          [:li "humetty/voided ordinaries, e.g. 'or, a pale humetty voided azure'"]
          [:li "ordinary groups, e.g. 'or, three barrulets gules'"]
          [:li "charges and charge groups with fimbriation, e.g. 'or, in chief three estoiles of 8 points sable', 'or, 12 roundels sable in orle' (or 'in annullo', 'palewise', etc.)"]
          [:li "semy, e.g. 'azure semy fleur-de-lis or'"]
          [:li "tincture/field referencing, e.g. 'per pale or and azure, a fess azure charged with a roundel of the field' (or 'of the first')"]
          [:li "various modifiers for ordinaries and charges that support it, e.g. 'reversed', 'mirrored', 'truncated', 'throughout', 'enhanced', etc."]]
         [:p "Additional changes and new features:"]
         [:ul
          [:li "the site now has a language selection, so far only English and German are really populated, but any help is welcome at "
           [:a {:href "https://crowdin.com/project/heraldicon"
                :target "_blank"} "Crowdin"]]
          [:li "pages and dialogs that list objects now have preview images and better filtering options"]
          [:li "bordures and orles can have line styles now"]
          [:li "furs now have an option to offset and scale them"]
          [:li "there's a dedicated star/mullet ordinary now, which allows an arbitrary number of points and wavy rays (to make an estoile)"]
          [:li "users can have an avatar now, for the moment it uses the Gravatar ("
           [:a {:href "https://gravatar.com"
                :target "_blank"} "Gravatar"] ") for the user's email address"]
          [:li "more escutcheon options, in particular the flag shape now can be used with all the usual and arbitrary aspect ratios, and it can have a configurable swallow tail"]
          [:li "allow arbitrary metadata for charges, arms, collections, which is considered for searching"]
          [:li "various element options allow a wider range of values and/or got better defaults"]
          [:li "performance improvements and bugfixes"]]]
        [:div
         (release-image "/img/2022-04-07-release-update.png")]]])}

   {:id "2022-01-09"
    :title "Happy new year!"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:p "The site has a new name: " [:b "Heraldicon"]]
         [:p "Since there is at least one other group and a well-known heraldic artist who already use the name 'Digital Heraldry', the old name collided somewhat with them. "
          "'Heraldry' also is rather generic on its own, making it hard to look for it among many heraldry-related websites."]
         [:p "I hope the new name identifies the project more directly and is easier to find if anyone wants to refer to the website."]
         [:p "Note: all export links remain unchanged and all links to the old website should redirect to the new website. Let me know if you see any problems after the move. :)"]]]])}

   {:id "2021-11-25"
    :title "Bordure/orle, in orle charge groups, chevronny"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:p "Changes and new features:"]
         [:ul
          [:li "Bordures and orles"
           [:ul
            [:li "there now are bordure and orle ordinaries with arbitrary thickness/distance to the edge"]
            [:li "so far they don't allow line styles other than straight, which follows the surrounding field edge"]]]

          [:li "Humetty/couped and voided ordinaries"
           [:ul
            [:li "ordinaries can now be made humetty/couped"]
            [:li "ordinaries can now be voided, this also honours the line style of the surrounding ordinary"]
            [:li "ordinaries also can be humetty/couped and voided"]]]

          [:li "Charge groups can be arranged in orle"
           [:ul
            [:li "this allows arranging N charges in orle, based on the surrounding field edge"]
            [:li "use the distance and offset arguments to adjust where exactly the charges end up"]]]

          [:li "Others"
           [:ul
            [:li "chevronny now is a field partition, configurable in most aspects, like other field partitions"]
            [:li "fur fields now have an additional 'pattern scaling' option to scale, you've guessed it, the pattern, "
             "which is useful for artistic preferences or in charges, where smaller ermine spots make more sense"]
            [:li "fields are clickable again in the rendering to select them more easily"]
            [:li "landscapes can now be added if raster graphics are embedded in an SVG and uploaded as charge, this also can be used for diapering"]
            [:li "a new community theme 'Content Cranium' and a silly theme that transitions through ALL themes"]
            [:li "there are a few more escutcheon choices for Norman shields and flag variants"]
            [:li "the interface should be MUCH faster now due to a rewrite of large parts of it"]
            [:li "there's a dedicated "
             [:a {:href "https://discord.gg/EGbMW8dth2" :target "_blank"} "Discord"]
             " server now, where my username is " [:em "or#5915"]]]]

          [:li "Known issues"
           [:ul
            [:li "the path for the bordure/orle and charge group in orle positions sometimes can have unexpected glitches, if that happens try to resize it a little or change the distance to the field edge"]
            [:li "charges sometimes block underlying fields when trying to click them, that's a technical issue with the way the browser works, hopefully it can be addressed someday"]]]]]

        [:div
         (release-image "/img/2021-11-25-release-update.png")]]])}

   {:id "2021-10-25"
    :title "Supporters, compartments, mantling, charge library improvements"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:p "Changes and new features:"]
         [:ul
          [:li "Supporters, compartments, mantling"
           [:ul
            [:li "ornaments around the shield can now be added"]
            [:li "there are some default charges for supporters, compartment, and mantling"]
            [:li "any other existing charge or new custom charges can be used for this"]
            [:li "a mechanism allows rendering these charges behind and in front of the shield (see next point)"]]]

          [:li "charge library interface improvements"
           [:ul
            [:li "charges intended as ornaments can contain colour(s) that designate areas that separate background and foreground of the charge, so the shield can be rendered between them"]
            [:li "the charge library interface now allows highlighting colours to easily see which area of the charge is affected by configuring it"]
            [:li "a new shading option is available now, which can qualify colours with the level of shading it represents, so no alpha-transparency shadow/highlight has to be added manually for many existing SVGs"]
            [:li "colours can also be sorted by colour, function, and shading modifier, making it easier to group them while editing"]]]
          [:li "Known issues"
           [:ul
            [:li "mottos/slogans were moved into 'ornaments', and the achievement positioning system has been rewritten, unfortunately a "
             "migration preserving their position wasn't feasible for all cases, this means that "
             [:b "your mottos/slogans might have moved"] " so best check them and fix it"]]]]]

        [:div
         (release-image "/img/2021-10-25-release-update.png")]]])}

   {:id "2021-09-26"
    :title "German translation, undo/redo, quarter/canton/point ordinaries, fretty"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:p "Changes and new features:"]
         [:ul
          [:li "German translation"
           [:ul
            [:li "the entire interface is now available in German"]
            [:li "News and About section aren't translated for the time being, might do that at some point as well"]
            [:li "there might be some translations that are not ideal or awkward the way they are used, email me if you find such cases! :)"]]]

          [:li "all forms now support undo/redo"]
          [:li "added the quarter/canton ordinary"]
          [:li "added the point ordinary"]
          [:li "added fretty"]
          [:li "added more flag aspect ratios"]
          [:li "Bugfixes"
           [:ul
            [:li "the environments for many ordinaries and subfields were broken (they still need some work, but now most of them should be reasonable)"]
            [:li "a bunch of minor things in the UI and rendering"]]]]]]])}

   {:id "2021-08-20"
    :title "Helms/crests, mottos/slogans, ribbon editor + library"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:p "Changes and new features:"]
         [:ul
          [:li "Helms/crests"
           [:ul
            [:li "helmets, torses, crests or any combination of them can now be added"]
            [:li "multiple helms are supported next to each other, they auto resize"]
            [:li "for the helmets three new tinctures were introduced: light, medium, dark, but a helmet is just a charge, so it has a normal field that allows any other modifications of a field"]]]

          [:li "Ribbons and mottos/slogans"
           [:ul
            [:li "a ribbon library can now be used to (hopefully easily) create dynamic ribbons to be used with mottos and slogans"]
            [:li "ribbons try to be reusable for many cases, things like thickness, size, the split of the ribbon's ends, various text properties for its segments can be changed in the arms where they are used"]
            [:li "mottos/slogans can live below/above the escutcheon (or overlap it) and be tinctured (foreground, background, text)"]]]

          [:li "the escutcheon can now be rotated freely from -45° to 45°, helms will settle at the respective top corner"]
          [:li "the SVG/PNG export is improved, now using a headless Chromium in the backend to generate pretty much the same as the preview on the page"]
          [:li "the PNG export yields a higher resolution"]
          [:li "charges can be masked vertically (top or bottom) to be able to make demi-charges that are issuant from behind torses or ordinaries or other charges, as can be seen in the examples in the crest and the lion behind a thin fess"]]

         [:p "Known issues:"]
         [:ul
          [:li "helms and mottos are labeled 'alpha', because they are bound to change still, positioning might change in the future"]
          [:li "the ribbon editor definitely needs more work to be more user friendly or have better explanation"]
          [:li "the default position for helmet/torse/crest is somewhat sensible only for the default helmet and torse right now, for others it needs to be tweaked manually, this should improve in the future"]
          [:li "similarly slogans are positioned rather high above the escutcheon to make room for helm crests, even if there aren't any; again this should be smarter in the future"]
          [:li "fonts are embedded in exported SVGs, some editors/viewers won't display them correctly due to the different way some SVG markup is interpreted, Chrome should work"]]]

        [:div
         (release-image "/img/2021-08-20-release-update.png")]]])}

   {:id "2021-07-30"
    :title "UI rewrite, validation system, social media sharing"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:p "Changes and new features:"]
         [:ul
          [:li "UI rewrite"
           [:ul
            [:li "the nested components are gone, as they were confusing, hard to navigate, and caused annoying scrolling issues"]
            [:li "separate navigation tree and form for selected components"]
            [:li "big speed improvements, a lot of the UI interactions now take 1-5ms, where previously 100-200ms were commonplace"]
            [:li "the layout works a bit better on mobile devices in landscape more, but the target environment still is Chrome on a desktop"]
            [:li "the cogs next to options allow an easy jump back to the default value or in some cases the value inherited from other components"]
            [:li "sliders now have a text field next to it to update values with specific values"]]]

          [:li "a validation system to warn about components that might break rule of tincture and potentially other issues"]
          [:li "improved blazonry, it now makes more of an attempt of reporting the used charges in charge groups, and it includes fimbriation"]
          [:li "SVG/PNG exports now contain a short URL to reference the arms on Heraldicon"]
          [:li "pall ordinaries now can be added, with different fimbriation for each line and arbitrary issuing from anywhere"]]]

        [:div
         (release-image "/img/2021-07-30-release-update.png")]]])}

   {:id "2021-06-12"
    :title "Charge groups, public arms, collections, users"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:p "New features:"]
         [:ul
          [:li "support for charge groups, charges can be arranged in rows, columns, grids or arcs/circles"]
          [:li "various charge group presets for the most common arrangements"]
          [:li "users can now be browsed"]
          [:li "public arms and collections can now be browsed"]
          [:li "tincture modifiers 'orbed' and 'illuminated' added"]]
         [:p "Known issues:"]
         [:ul
          [:li "many charges render slowly"]
          [:li "charge groups should take the surrounding field better into account in some situations, more work needed"]
          [:li "charge group presets inspired by ordinaries use the grid and might not align perfectly with ordinaries, more work needed"]]]
        [:div
         (release-image "/img/2021-06-12-release-update.png")]]])}

   {:id "2021-06-06"
    :title "Collections, tags, filter"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:p "New features:"]
         [:ul
          [:li "support for collections, which can be used to group existing arms, to create rolls of arms, group drafts, display arms for a specific topic, etc."]
          [:li "arms, charges, and collections now also can be tagged arbitrarily, and these tags can then be used to filter for them or annotate some notes"]
          [:li "there are now separate options for escutcheon shadow and outline"]
          [:li "text in collections can be rendered with various fonts"]]
         [:p "Known issues:"]
         [:ul
          [:li "large collections might render slowly, now that there are multiple arms being rendered on the fly, it shows that some parts of the rendering engines are not optimized yet"]]]
        [:div
         (release-image "/img/2021-06-06-release-update.png")]]])}

   {:id "2021-05-09"
    :title "Cottising, labels, semy"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:p "New features:"]
         [:ul
          [:li "support for cottising, cottises can have their own line styles, fimbriation, and follow the parent ordinary's alignment"]
          [:li "labels ordinaries can now be added, they can be full length, truncated/couped, and support various emblazonment options"]
          [:li "the chevron interface has changed a bit again, now using the anchor system as well, so they can be issuant from anywhere"]
          [:li "semys with arbitrary charges are now possible, charges can be resized, rotated, the whole semy pattern layout also can be configured"]
          [:li "new line styles: rayonny (flaming), rayonny (spiked), wolf toothed"]
          [:li "lines can be aligned to their top, bottom, or middle"]]]
        [:div
         (release-image "/img/2021-05-09-release-update.png")]]])}

   {:id "2021-03-31"
    :title "Embowed/enarched, nonrepeating line styles"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:p "A big refactoring line styles, allowing line styles that are not pattern-based but extend across the full length of the line."]
         [:ul
          [:li "support of full-length line styles, such as bevilled, angled, and enarched"]
          [:li "new pattern-based line styles, such as potenty, embattled-grady, embattled-in-crosses, nebuly, fir-twigged, fir-tree-topped, thorny"]
          [:li "gore ordinaries, which also uses the enarched line style, but can use all other line styles as well"]
          [:li "bug fixes"]]]
        [:div
         (release-image "/img/2021-03-31-release-update.png")]]])}

   {:id "2021-03-16"
    :title "Chevron and pile"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:p "A total refactoring of angular alignment necessary for chevron and pile variants."]
         [:ul
          [:li "chevron ordinary and division variants issuant from chief, dexter, and sinister"]
          [:li "pile ordinary and division variants issuant from anywhere, pointing at specific points or the opposite edge"]
          [:li "edge detection of the surrounding field for piles is dynamic and works for any shape and orientation"]
          [:li "orientation based on an " [:b "origin"] " and an " [:b "anchor"]
           ", which allows precise construction of bends, chevrons, saltires, piles, etc."]
          [:li "new concept of " [:b "alignment"] " allows aligning ordinary edges with the chosen origin/anchor, "
           "not just the middle axis"]
          [:li "more support of arbitrary SVGs for the charge library"]
          [:li "WappenWiki and Wikimedia licensing presets for charge attribution"]
          [:li "bug fixes"]]]
        [:div
         (release-image "/img/2021-03-16-release-update.png")]]])}

   {:id "2021-03-07"
    :title "Paly and fimbriation"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:p "The main new features are:"]
         [:ul
          [:li "paly/barry/bendy/chequy/lozengy field divisions with configurable layout"]
          [:li "quarterly division of MxN fields"]
          [:li "vairy treatment and its variations"]
          [:li "potenty treatment and its variations"]
          [:li "papellony and masoned treatment"]
          [:li "line fimbriation (single and double) with configurable thickness, which can handle intersecting with itself and optional outlines"]
          [:li "charge fimbriation with configurable thickness"]
          [:li "shininess and shield textures, which also can be used as displacement maps for various effects"]
          [:li "optional alpha transparency shadow/highlight in charges, which is applied dynamically after rendering "
           "the charge field, i.e. it works with any tincture or treatment or division of the charge's field"]
          [:li "bug fixes"]]]
        [:div
         (release-image "/img/2021-03-07-release-update.png")]]])}

   {:id "2021-02-08"
    :title "First release"
    :content
    (fn []
      [[:div.release-row
        [:div.info
         [:p "Following a browser-only prototype, this site now has a backend, where users can build a public "
          "and private library of charges and arms."]
         [:p
          "Features include:"]
         [:ul
          [:li "various escutcheons with their own relevant points, e.g. fess, honour, nombril, etc."]
          [:li "several divisions"]
          [:li "several ordinaries"]
          [:li "several line styles"]
          [:li "some common charge shapes"]
          [:li "lion and wolf charges in various attitudes"]
          [:li "counterchanged ordinaries and charges"]
          [:li "ermine-like furs"]
          [:li "dimidiation"]
          [:li "very basic blazoning of the constructed arms"]
          [:li "tincture themes, including hatching"]
          [:li "licensing information and attribution can be given for charges and arms, and indeed is required to make either public"]
          [:li "SVG/PNG export of saved arms; the saving is necessary for proper attribution"]]]
        [:div.info
         (release-image "/img/2021-02-08-release-update.png")]]])}])

(defn- post-heading [id title]
  [:h3 {:id id}
   [:a {:href (str "#" id)
        :style {:color "inherit"
                :text-decoration "none"}}
    title]])

(defn view []
  (rf/dispatch [::title/set :string.text.title/home])
  (into
   [:div
    {:style {:padding "10px"
             :text-align "justify"
             :min-width "30em"
             :max-width "60em"
             :margin "auto"}}
    [:div.release-row
     [:div.info
      [:h2 "News"
       [:a {:href "/atom.xml"
            :target "_blank"
            :style {:margin-left "0.5em"
                    :position "relative"
                    :top "1px"}}
        [:i.fas.fa-rss-square]]]
      [:p "In many cases new features are rolled out incrementally, without big release. But now and then I'll group some features and new development and post an update here, so it is easy to stay informed."]]]]
   (mapcat (fn [{:keys [id title content] :as post}]
             (into [(post-heading id (str (post-date post) " - " title))]
                   (content)))
           posts)))
